import { useGetUserProfileByAddress } from '@/api/query'
import { AvatarCircle } from '@/components/atoms/Avatar'
import { SHU_DENOM } from '@/constants/constants'
import { FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import { useAudio, SOUNDS } from '@/context/AudioProvider'
import { GameStage, Player, useJackpot } from '@/context/JackpotProvider'
import { JackpotModals, useAppModal } from '@/context/ModalProvider'
import { useSession } from '@/context/SessionProvider'
import { generateSetOfRandomNumbers } from '@/helpers/generateSetOfRandomNumbers'
import { getAvatarUrl } from '@/helpers/getAvatarUrl'
import { Box, Flex, Text } from '@chakra-ui/react'
import { useState, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'next-i18next/pages'
import { reaction } from 'mobx'
import { Image } from '@/components/HOC/Image'
import { Track, TrackController } from '@/components/molecules/Track/Track'
import { Observer } from 'mobx-react'
import { motion } from 'framer-motion'
import PointerIcon from '@/public/assets/images/double/dagger.png'
import { useApolloClient } from '@apollo/client'
import { Amount } from '@/components/atoms/Amount/Amount'
import { TokenMoney } from '@ninjablaze/sdk-ts'

export const JackpotRollingTrack = () => {
  const ref = useRef<TrackController>({} as TrackController)
  const [isTrackInitialized, setTrackInitialized] = useState(false)
  const jackpot = useJackpot()
  const list = useMemo(() => generatePlayerList(jackpot.state.players, jackpot.state.start_block), [])
  const audio = useAudio()
  const firstRender = useRef(true)
  const [isRoundFinished, setRoundFinished] = useState(false)
  const appoloClient = useApolloClient()

  useEffect(() => {
    return () => {
      audio.play(SOUNDS.NEW_ROUND)
    }
  }, [])

  useEffect(() => {
    if (!isTrackInitialized) {
      return
    }

    switch (jackpot.state.state) {
      case GameStage.DRAWING:
        jackpot.setRoundStarted()
        ref.current?.start()
        break
      case GameStage.FINISHED:
        if (firstRender.current) {
          ref.current?.startStopIndex(
            jackpot.state.finishedTimestamp,
            list.findIndex((player) => jackpot.state.player_winner === player.address)
          )
        } else {
          ref.current?.stopIndex(
            jackpot.state.finishedTimestamp,
            list.findIndex((player) => jackpot.state.player_winner === player.address)
          )
        }

        break
    }

    const disposer = reaction(
      () => jackpot.state.state,
      (state) => {
        switch (state) {
          case GameStage.INIT:
            setRoundFinished(false)
            appoloClient.refetchQueries({
              include: ['GetJackpotGames'],
            })
            break
          case GameStage.DRAWING:
            audio.play(SOUNDS.ROLLING_STARTS)
            break
          case GameStage.FINISHED:
            if (firstRender.current) {
              ref.current?.startStopIndex(
                jackpot.state.finishedTimestamp,
                list.findIndex((player) => jackpot.state.player_winner === player.address)
              )
            } else {
              ref.current?.stopIndex(
                jackpot.state.finishedTimestamp,
                list.findIndex((player) => jackpot.state.player_winner === player.address)
              )
            }
            break
        }
      }
    )

    firstRender.current = false

    return () => disposer()
  }, [isTrackInitialized])

  return (
    <Box position={'absolute'} left={'50%'} transform={'translateX(-50%)'} mt={5}>
      <Box position={'relative'}>
        <Box overflow={'hidden'} py={12} w={'1290px'} boxSizing="border-box">
          <Track
            ref={ref}
            width={1290}
            elements={list}
            renderElement={({ field }) => (
              <Box height={'78px'} px={1}>
                <PlayerBox player={field} />
              </Box>
            )}
            elementWidth={86}
            initialPosition={0}
            isTrackInitialized={isTrackInitialized}
            setTrackInitialized={() => setTrackInitialized(true)}
            panelChangeCallback={() => {
              audio.play(SOUNDS.ROLLING_STEP)
            }}
            roundFinishedCallback={() => {
              setRoundFinished(true)
            }}
          />
          <Pointer />
        </Box>
      </Box>
      <GameStageElement isRoundFinished={isRoundFinished} />
    </Box>
  )
}

const Pointer = () => {
  const store = useJackpot()

  const variants = {
    show: {
      opacity: 1,
    },
    hide: {
      opacity: 0,
    },
  }

  return (
    <Observer>
      {() => {
        return (
          <motion.div
            key="animation-on-state"
            variants={variants}
            animate={store.state.isRoundFinished ? 'hide' : 'show'}
            transition={{ delay: 0.5 }}
          >
            <Image
              top={0}
              pos={'absolute'}
              left={'calc(50% - 3px)'}
              transform={'translateX(-50%) rotate(180deg)'}
              src={PointerIcon}
              alt={''}
            />
            <Image
              display={{ base: 'none', md: 'block' }}
              pos={'absolute'}
              bottom={0}
              left={'calc(50% + 2px)'}
              transform={'translateX(-50%)'}
              src={PointerIcon}
              alt={''}
            />
          </motion.div>
        )
      }}
    </Observer>
  )
}

const GameStageElement = ({ isRoundFinished }: { isRoundFinished: boolean }) => {
  const { t } = useTranslation()

  return (
    <Box mt={{ base: 0, md: 2 }}>
      {isRoundFinished ? (
        <WinnerPanel />
      ) : (
        <Flex justifyContent={'center'}>
          <Text fontSize={'lg'} fontWeight={700} px={6} py={3} rounded={'xl'} bg={'#1D1C4912'}>
            {t('Jackpot.messages.rolling')}
          </Text>
        </Flex>
      )}
    </Box>
  )
}

const WinnerPanel = () => {
  const jackpot = useJackpot()
  const { account } = useSession()
  const player = jackpot.state.players.find((player) => player?.address === jackpot.state.player_winner)
  const { data } = useGetUserProfileByAddress(jackpot.state.player_winner!)
  const { t } = useTranslation()
  const modal = useAppModal()
  const audio = useAudio()

  useEffect(() => {
    if (player?.address === account?.injectiveAddress) {
      audio.play(SOUNDS.PLAYER_WON)
      modal.openModalNew(JackpotModals.JackpotWinnerModal, { roundData: jackpot.state })
    } else if (jackpot.state.players.find((player) => player.address === account?.injectiveAddress)) {
      audio.play(SOUNDS.PLAYER_LOST)
    } else {
      audio.play(SOUNDS.PLAYER_WON)
    }
  }, [])

  return (
    <Flex
      mt={{ base: 0, lg: 2 }}
      gap={{ base: 1, lg: 2 }}
      justifyContent={'center'}
      alignItems={'center'}
      flexDir={{ base: 'column', lg: 'row' }}
    >
      <Flex gap={{ base: 1, lg: 2 }} justifyContent={'center'} alignItems={'center'}>
        <AvatarCircle src={data?.user?.profileAvatar || ''} offset={2} size="sm" />
        <Text color={'ninja.100'} fontWeight={700}>
          {data?.user?.profileUserName}
        </Text>
        <Box rounded={'xl'} bg={'#2EB47C'} fontWeight={700} py={1.5} px={2.5}>
          {t('Jackpot.winner')}
        </Box>
      </Flex>
      <Flex flexShrink={0} gap={2} justifyContent={'center'} alignItems={'center'}>
        <FiatOrCryptoAmount
          iconSize={6}
          value={new TokenMoney(jackpot.state.pool, SHU_DENOM)}
          wrapperStyles={{ fontSize: 'xl', fontWeight: 700 }}
        />
      </Flex>
      <Flex gap={1.5} py={2} px={3} rounded={'full'} bg={'blackAlpha.200'} display={{ base: 'none', lg: 'flex' }}>
        <Text fontWeight={500} fontSize={'sm'}>
          {t('Jackpot.chance')}
        </Text>
        <Text color={'#68F276'} fontWeight={700} fontSize={'sm'}>
          {player?.chance}%
        </Text>
      </Flex>
    </Flex>
  )
}

const PlayerBox = ({ player }: { player: Player }) => {
  const { data } = useGetUserProfileByAddress(player?.address)

  return (
    <Box bg={'ninja.500'} width={'78px'} height={'78px'} rounded={'xl'} overflow={'hidden'} flexShrink={0}>
      <Image
        width={'78'}
        height={'78'}
        src={getAvatarUrl(data?.user?.profileAvatar || 'bafkreibyuphvv2oayjnchhmgduwkk5xvrauntxzcqqbmjandfps4azxkr4')}
        alt={''}
      />
    </Box>
  )
}

const generatePlayerList = (players: Player[], seed: number) => {
  const result: Player[] = []

  const TOTAL_TILES = 30

  players.forEach((player) => {
    let tiles = Math.ceil(TOTAL_TILES * (parseFloat(player.chance) / 100))
    result.push(...Array.from({ length: tiles }, () => player))
  })

  const array = shuffleArray(result, seed)

  return array
}

function shuffleArray<T>(array: Array<T>, seed: number) {
  const randomNumbers = generateSetOfRandomNumbers(array.length, seed)

  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(randomNumbers[i] * (i + 1))
    ;[array[i], array[j]] = [array[j], array[i]]
  }

  return array
}
